import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../swof/api";
import { AdminShell } from "./ProtectedRoute";
import { InlineEditor, RichEditor } from "./RichText";
import {
  Duim, Foutmelding, Kopje, Titel, Uploader, invoerStijl, knopStijl, wegStijl, mediaKaart,
} from "./bouwstenen";

const INDUCTEE_VELDEN = [
  ["nummer", "Nummer", "tekst"],
  ["naam", "Naam", "tekst"],
  ["award", "Award (leeg = gewone inductee)", "tekst"],
  ["jaar", "Jaar", "getal"],
  ["korte_tekst", "Korte tekst op de kaart", "rich"],
  ["lede", "Lede in het venster", "inline"],
  ["body", "Volledige tekst", "rich"],
];

const BESTUUR_VELDEN = [
  ["nummer", "Nummer", "tekst"],
  ["voornaam", "Voornaam", "tekst"],
  ["achternaam", "Achternaam (wordt cursief)", "tekst"],
  ["rol", "Rol", "tekst"],
  ["tag", "Regel onder de rol", "inline"],
  ["dek", "Cursieve samenvatting", "inline"],
  ["citaat", "Citaat (optioneel)", "inline"],
];

function Regels({ label, waarden, onChange, testId, rich }) {
  const lijst = waarden || [];
  const zet = (i, v) => onChange(lijst.map((x, j) => (j === i ? v : x)));
  return (
    <div style={{ marginBottom: 30, paddingBottom: 20, borderBottom: "1px solid var(--line)" }}>
      <Kopje>{label}</Kopje>
      {lijst.map((waarde, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--gold)", paddingTop: 14, width: 24 }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <div style={{ flex: 1 }}>
            <InlineEditor waarde={waarde} onChange={(v) => zet(i, v)} testId={`${testId}-${i}`} />
          </div>
          <button onClick={() => onChange(lijst.filter((_, j) => j !== i))}
            data-testid={`${testId}-weg-${i}`} style={{ ...wegStijl, marginTop: 34 }}>Weg</button>
        </div>
      ))}
      <button onClick={() => onChange([...lijst, ""])} data-testid={`${testId}-toevoegen`} style={knopStijl}>
        Regel toevoegen
      </button>
    </div>
  );
}

export default function PersoonEditPage({ soort }) {
  const { persoonId } = useParams();
  const navigate = useNavigate();
  const isInductee = soort === "inductees";
  const [content, setContent] = useState(null);
  const [item, setItem] = useState(null);
  const [fout, setFout] = useState("");
  const [bezig, setBezig] = useState(false);
  const [opgeslagen, setOpgeslagen] = useState(false);

  const laad = () => api.get("/admin/site").then(({ data }) => {
    setContent(data);
    const bron = isInductee ? data.inductees : data.bestuursleden;
    setItem(bron.find((p) => p.id === persoonId) || null);
  });

  useEffect(() => {
    document.body.classList.add("swof");
    laad();
    return () => document.body.classList.remove("swof");
  }, [persoonId]);

  const melden = (e) => setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  if (!content || !item) {
    return <AdminShell title="Bewerken"><div data-testid="persoon-laden">Laden…</div></AdminShell>;
  }
  const media = mediaKaart(content.media);
  const velden = isInductee ? INDUCTEE_VELDEN : BESTUUR_VELDEN;
  const fotos = (content.inductee_fotos || []).filter((f) => f.inductee_id === item.id);
  const zet = (naam, waarde) => setItem((p) => ({ ...p, [naam]: waarde }));

  const opslaan = async () => {
    setBezig(true);
    setFout("");
    const payload = {};
    velden.forEach(([naam]) => { payload[naam] = item[naam] ?? ""; });
    payload.portret_id = item.portret_id || null;
    payload.zichtbaar = Boolean(item.zichtbaar);
    if (isInductee) payload.video_id = item.video_id || null;
    else {
      payload.bullets = item.bullets || [];
      payload.body = item.body || [];
    }
    try {
      await api.put(`/admin/${soort}/${item.id}`, { velden: payload });
      await laad();
      setOpgeslagen(true);
      setTimeout(() => setOpgeslagen(false), 2500);
    } catch (e) { melden(e); } finally { setBezig(false); }
  };

  const koppelPortret = async (nieuw) => {
    zet("portret_id", nieuw[0].id);
    try {
      await api.put(`/admin/${soort}/${item.id}`, { velden: { portret_id: nieuw[0].id } });
      await laad();
    } catch (e) { melden(e); }
  };

  const voegFotosToe = async (nieuw) => {
    try {
      await api.post(`/admin/inductees/${item.id}/fotos`, { media_ids: nieuw.map((m) => m.id) });
      await laad();
    } catch (e) { melden(e); }
  };

  const verwijderFoto = async (foto) => {
    try {
      await api.delete(`/admin/inductee-fotos/${foto.id}`);
      await laad();
    } catch (e) { melden(e); }
  };

  const naam = isInductee ? item.naam : `${item.voornaam} ${item.achternaam}`;

  return (
    <AdminShell title={isInductee ? "Inductee" : "Bestuurslid"} actions={
      <button onClick={() => navigate(`/admin/${soort}`)} data-testid="persoon-terug" style={knopStijl}>
        ← Lijst
      </button>
    }>
      <Titel onder="Wijzigingen komen in het concept. Bekijk het resultaat via Voorbeeld.">{naam}</Titel>
      <Foutmelding fout={fout} />

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", marginBottom: 32, flexWrap: "wrap" }}>
        <Duim media={media[item.portret_id]} hoogte={150} />
        <div>
          <Kopje>Portret</Kopje>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select value={item.portret_id || ""} data-testid="persoon-portret"
              onChange={(e) => zet("portret_id", e.target.value || null)}
              style={{ ...invoerStijl, maxWidth: 340 }}>
              <option value="" style={{ color: "#111" }}>— geen —</option>
              {content.media.map((m) => (
                <option key={m.id} value={m.id} style={{ color: "#111" }}>{m.bestandsnaam}</option>
              ))}
            </select>
            <Uploader testId="persoon-portret-upload" label="Portret uploaden" meerdere={false}
              soort="portret" onKlaar={koppelPortret} />
          </div>
        </div>
      </div>

      {velden.map(([naamVeld, label, kind]) => (
        <div key={naamVeld} style={{ marginBottom: 30, paddingBottom: 20, borderBottom: "1px solid var(--line)" }}>
          <Kopje>{label}</Kopje>
          {kind === "rich" ? (
            <RichEditor waarde={item[naamVeld] || ""} onChange={(v) => zet(naamVeld, v)} testId={`veld-${naamVeld}`} />
          ) : kind === "inline" ? (
            <InlineEditor waarde={item[naamVeld] || ""} onChange={(v) => zet(naamVeld, v)} testId={`veld-${naamVeld}`} />
          ) : (
            <input value={item[naamVeld] ?? ""} data-testid={`veld-${naamVeld}`}
              type={kind === "getal" ? "number" : "text"}
              onChange={(e) => zet(naamVeld, e.target.value)}
              style={{ ...invoerStijl, maxWidth: kind === "getal" ? 180 : "100%" }} />
          )}
        </div>
      ))}

      {isInductee ? (
        <>
          <div style={{ marginBottom: 30, paddingBottom: 20, borderBottom: "1px solid var(--line)" }}>
            <Kopje>Video</Kopje>
            <select value={item.video_id || ""} data-testid="veld-video_id"
              onChange={(e) => zet("video_id", e.target.value || null)}
              style={{ ...invoerStijl, maxWidth: 460 }}>
              <option value="" style={{ color: "#111" }}>— geen —</option>
              {content.videos.map((v) => (
                <option key={v.id} value={v.id} style={{ color: "#111" }}>
                  {v.bron} · {v.titel}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 30, paddingBottom: 20, borderBottom: "1px solid var(--line)" }}>
            <Kopje>Foto's van de avond</Kopje>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              {fotos.map((f) => (
                <div key={f.id} data-testid={`inductee-foto-${f.id}`} style={{ position: "relative" }}>
                  <Duim media={media[f.media_id]} hoogte={78} ratio="3 / 2" />
                  <button onClick={() => verwijderFoto(f)} data-testid={`inductee-foto-weg-${f.id}`}
                    style={{ ...wegStijl, marginTop: 6, width: "100%" }}>Weg</button>
                </div>
              ))}
            </div>
            <Uploader testId="inductee-fotos-upload" label="Foto's toevoegen" onKlaar={voegFotosToe} />
          </div>
        </>
      ) : (
        <>
          <Regels label="Feitenregels (genummerd)" waarden={item.bullets} testId="veld-bullets"
            onChange={(v) => zet("bullets", v)} />
          <Regels label="Alinea's van de biografie" waarden={item.body} testId="veld-body"
            onChange={(v) => zet("body", v)} />
        </>
      )}

      <div style={{
        position: "sticky", bottom: 0, background: "var(--ink)", padding: "18px 0",
        borderTop: "1px solid var(--line)", display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap",
      }}>
        <button onClick={opslaan} disabled={bezig} data-testid="persoon-opslaan" style={{
          padding: "14px 30px", background: bezig ? "rgba(201,169,97,0.5)" : "var(--gold)",
          color: "var(--ink)", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.22em",
          textTransform: "uppercase", border: "none",
        }}>{bezig ? "Opslaan…" : "Opslaan"}</button>
        <label style={{
          display: "flex", alignItems: "center", gap: 10, fontFamily: "var(--mono)", fontSize: 10,
          letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.8,
        }}>
          <input type="checkbox" checked={Boolean(item.zichtbaar)} data-testid="veld-zichtbaar"
            onChange={(e) => zet("zichtbaar", e.target.checked)} />
          Zichtbaar op de site
        </label>
        {opgeslagen && (
          <span data-testid="persoon-opgeslagen" style={{
            fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em",
            textTransform: "uppercase", color: "var(--gold)",
          }}>Opgeslagen in het concept ✓</span>
        )}
      </div>
    </AdminShell>
  );
}
